import * as React from 'react';
import { Check, ChevronDown, Lock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { Badge } from './badge';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** An unselected value that cannot be added (unlike `lockedValues`): greyed, `hint` in place of the check. */
  disabled?: boolean;
  hint?: string;
}

interface MultiSelectProps {
  options?: MultiSelectOption[];
  selected?: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  allOption?: MultiSelectOption | null; // e.g. { value: '*', label: 'Tous' }
  className?: string;
  disabled?: boolean;
  // Opt-in: renders a search input inside the popover to filter options
  // client-side. Meant for long option lists (hundreds of entries).
  searchable?: boolean;
  // The trigger is a button, not a form control, so a sibling <Label htmlFor> cannot reach it.
  // Callers name it here instead, otherwise it announces as an unlabelled button.
  'aria-label'?: string;
  // The trigger is the control, so the id and the error message belong on it and not on the
  // container — an aria-describedby a screen reader reads off a <div> is one it never announces.
  // No `aria-invalid`: role `button` does not support it, and the `role="combobox"` trigger that
  // would is the ARIA pattern L2b deferred to its own ticket.
  id?: string;
  'aria-describedby'?: string;
  // Summary mode only: text drawn inside the trigger before the value ("Pays  Tous ▾").
  label?: string;
  // Opt-in: compact trigger reading "Tous" / the one label / "N selected" instead of badges.
  summary?: boolean;
  // Opt-in: the trigger joins the selected labels and adds a count pill past one, and the chip
  // list below is not rendered. For a consumer that draws its own selected rows — the technician's
  // geographic scope. Left off everywhere else: the other seven consumers keep the count.
  joinedLabels?: boolean;
  // Opt-in: values the caller imposes. They stay tickable-on and readable but cannot be removed —
  // the consumer that sets them owns their meaning (the « À qualifier » tab's definition). It does
  // not select them: the parent still passes them in `selected`. Absent, nothing is locked.
  lockedValues?: string[];
}

// Case- and diacritics-insensitive matching ("marseille" finds "Marseille", "sud" finds "Süd").
// Combining-diacritics range instead of \p{Diacritic}: the ts target predates the unicode flag.
const normalize = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options = [],
      selected = [],
      onChange,
      placeholder,
      allOption = null,
      className,
      disabled = false,
      searchable = false,
      'aria-label': ariaLabel,
      id,
      'aria-describedby': ariaDescribedBy,
      label,
      summary = false,
      joinedLabels = false,
      lockedValues = [],
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const resolvedPlaceholder = placeholder ?? t('Select...');
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const containerRef = React.useRef<HTMLDivElement>(null);

    // A stale query must not survive a close/reopen cycle.
    React.useEffect(() => {
      if (!open) setQuery('');
    }, [open]);

    const visibleOptions =
      searchable && query.trim()
        ? options.filter((option) => normalize(option.label).includes(normalize(query.trim())))
        : options;

    const isAllSelected = !!allOption && selected.includes(allOption.value);

    const isLocked = (value: string): boolean => lockedValues.includes(value);

    // The two "all" branches replace the whole selection; re-append the locked values they hold,
    // and only those — a locked value the parent never selected must not become selected here.
    const keepLocked = (values: string[]): string[] => [
      ...values,
      ...lockedValues.filter((v) => selected.includes(v) && !values.includes(v)),
    ];

    const isOptionDisabled = (value: string): boolean => !!options.find((o) => o.value === value)?.disabled;

    const handleToggle = (value: string) => {
      if (disabled || isLocked(value) || isOptionDisabled(value)) return;

      if (allOption && value === allOption.value) {
        // Toggle "all" option
        onChange(keepLocked(isAllSelected ? [] : [allOption.value]));
      } else if (isAllSelected) {
        // If "all" was selected, switch to individual selection
        onChange(keepLocked([value]));
      } else if (selected.includes(value)) {
        onChange(selected.filter((v) => v !== value));
      } else {
        onChange([...selected, value]);
      }
    };

    const handleRemove = (value: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (isLocked(value)) return;
      onChange(selected.filter((v) => v !== value));
    };

    // Only a mousedown outside closed it. The trigger is the first button in the container.
    const handleEscape = (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.key !== 'Escape' || !open) return;
      event.stopPropagation();
      setOpen(false);
      containerRef.current?.querySelector('button')?.focus();
    };

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const displayValue = isAllSelected && allOption ? allOption.label : null;
    const hasSelection = !isAllSelected && selected.length > 0;
    const showBadges = hasSelection && !joinedLabels;
    const labelOf = (value: string): string => options.find((o) => o.value === value)?.label ?? value;

    const summaryValue = (): string => {
      if (displayValue) return displayValue;
      if (selected.length === 0) return resolvedPlaceholder;
      if (selected.length === 1) return options.find((o) => o.value === selected[0])?.label ?? selected[0];
      return t('{{count}} selected', { count: selected.length });
    };

    return (
      <div
        ref={containerRef}
        className={cn('relative', className)}
        data-list-open={open ? '' : undefined}
        {...props}
        onKeyDown={handleEscape}
      >
        <button
          ref={ref}
          type="button"
          id={id}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          // Safari does not focus a clicked button; Escape needs focus inside the field.
          onClick={(event) => {
            if (disabled) return;
            event.currentTarget.focus();
            setOpen(!open);
          }}
          className={cn(
            summary
              ? 'inline-flex h-[38px] items-center gap-[7px] whitespace-nowrap rounded-[9px] border border-input bg-background px-3 text-[13px] focus:outline-none'
              : 'flex min-h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring',
            summary && !disabled && 'hover:border-zinc-300 dark:hover:border-zinc-600',
            // The open ring replaces the focus ring: twMerge keeps both and the variant would win.
            // Zinc, not the brand ring: the design's open state is its monochrome primary over a hover-grey halo.
            summary && (open ? 'border-zinc-900 ring ring-zinc-100 dark:border-zinc-300 dark:ring-zinc-800' : 'focus-visible:ring-1 focus-visible:ring-ring'),
            disabled && 'cursor-not-allowed opacity-50',
            !disabled && 'cursor-pointer'
          )}
        >
          {summary && label && <span className="text-muted-foreground">{label}</span>}
          {summary ? (
            <span className="font-medium text-foreground">{summaryValue()}</span>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              {displayValue && <span className="truncate text-sm">{displayValue}</span>}
              {/* A count, not the joined labels: the chips below already carry them, and the
                  trigger repeating them showed every consumer the same values twice. */}
              {hasSelection && (
                <span className="truncate text-sm">
                  {joinedLabels
                    ? selected.map(labelOf).join(', ')
                    : t('{{count}} selected', { count: selected.length })}
                </span>
              )}
              {joinedLabels && selected.length > 1 && (
                <span className="shrink-0 rounded-md bg-zinc-100 px-1.5 text-[11px] font-semibold leading-[18px] text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                  {selected.length}
                </span>
              )}
              {!displayValue && !hasSelection && (
                <span className="truncate text-muted-foreground">{resolvedPlaceholder}</span>
              )}
            </div>
          )}
          <ChevronDown
            className={cn(
              summary ? 'h-4 w-4 shrink-0 text-muted-foreground/70' : 'h-4 w-4 opacity-50 shrink-0 ml-2',
              open && 'rotate-180 transition-transform'
            )}
          />
        </button>

        {/* Outside the trigger, not inside it: a <button> nested in a <button> is invalid HTML
            (React says so in console), unreachable by keyboard and ambiguous to a screen reader.
            Zinc rather than bg-secondary, which tailwind.config pins to #ffffff in both themes
            while its foreground follows the theme — white on white in dark. */}
        {showBadges && !summary && (
          <ul className="mt-1.5 flex flex-wrap gap-1">
            {selected.map((value) => (
              <li key={value}>
                <Badge
                  variant="secondary"
                  className="text-xs px-1.5 py-0 bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
                >
                  {labelOf(value)}
                  {/* Read-only or locked: no button at all, rather than one announced and inert.
                      The X carries no text, so it needs its own name. */}
                  {!disabled && !isLocked(value) && (
                    <button
                      type="button"
                      aria-label={t('Remove {{label}}', { label: labelOf(value) })}
                      onClick={(e) => handleRemove(value, e)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              </li>
            ))}
          </ul>
        )}

        {open && !disabled && (
          <div
            className={
              summary
                ? 'absolute left-0 top-full z-50 mt-1 w-max max-w-[min(28rem,calc(100vw-2rem))] rounded-[10px] border bg-popover p-[5px] text-popover-foreground shadow-lg'
                // top-full, now that the chips sit between the trigger and here: without it the
                // popover takes its static position and drops below them.
                : 'absolute left-0 top-full z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md'
            }
          >
            {searchable && (
              <div className="border-b p-1">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('Search…')}
                  aria-label={t('Search…')}
                  autoFocus
                  className="w-full rounded-sm border-0 bg-transparent px-2 py-1.5 text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
            )}
            <div className={cn('overflow-y-auto', summary ? 'max-h-[296px] p-0' : 'max-h-60 p-1')}>
              {allOption && (
                <>
                  {/* Keep focus in the field: a click on a row must not blur it (Escape inside a dialog). */}
                  <div
                    onClick={() => handleToggle(allOption.value)}
                    onMouseDown={(event) => event.preventDefault()}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                      summary && 'min-h-[34px] rounded-[7px] py-1.5 pl-[11px] pr-9 text-[13px]',
                      isAllSelected && (summary ? 'font-semibold' : 'bg-accent')
                    )}
                  >
                    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                      {isAllSelected && <Check className="h-4 w-4" />}
                    </span>
                    <span className={cn('font-medium', summary && isAllSelected && 'font-semibold')}>{allOption.label}</span>
                  </div>
                  <div className="-mx-1 my-1 h-px bg-muted" />
                </>
              )}
              {searchable && query.trim() && visibleOptions.length === 0 && (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  {t('No data available')}
                </div>
              )}
              {visibleOptions.map((option) => {
                const isSelected = selected.includes(option.value);
                const isDisabled = isAllSelected || !!option.disabled;
                const locked = isLocked(option.value);

                return (
                  <div
                    key={option.value}
                    onClick={() => !isDisabled && handleToggle(option.value)}
                    onMouseDown={(event) => event.preventDefault()}
                    aria-disabled={locked || option.disabled || undefined}
                    title={locked ? t('This choice cannot be removed') : undefined}
                    className={cn(
                      'relative flex select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none',
                      // The design marks the picked option by weight and check, never by a filled row.
                      summary && 'min-h-[34px] rounded-[7px] py-1.5 pl-[11px] pr-9 text-[13px] font-medium',
                      isDisabled
                        ? 'cursor-not-allowed opacity-50'
                        // Locked stays fully readable — it is a current value, not an unavailable
                        // one; only the pointer and the hover say it cannot be touched.
                        : locked
                          ? 'cursor-not-allowed'
                          : 'cursor-pointer hover:bg-accent hover:text-accent-foreground',
                      isSelected && !isDisabled && (summary ? 'font-semibold' : 'bg-accent')
                    )}
                  >
                    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                      {locked ? (
                        <Lock className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                      ) : (
                        isSelected && <Check className="h-4 w-4" />
                      )}
                    </span>
                    <span className="flex flex-col">
                      <span>{option.label}</span>
                      {option.hint && <span className="text-xs text-muted-foreground">{option.hint}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';

export { MultiSelect };
